import { Fragment, useEffect, useRef, useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical } from "@fortawesome/free-solid-svg-icons";

import Popper from "components/Popper";
import { Option } from "components/OptionsCard/typings";
import dropdownStyles from "components/OptionsCard/styles.module.scss";

import styles from "./officialLetters.module.scss";

interface Props {
  options: Option[];
}

// Menu „więcej" wiersza pisma / przesyłki — toolbox Magistratu (Edytuj / Podpisz / Drukuj /
// Przekaż do wysyłki / Popraw …) pokazywany po najechaniu na wiersz; tutaj w menu kebab.
// Wygląd jak wspólny Dropdown (OptionsCard), ale zamykanie obsługujemy sami: Dropdown kładzie
// pełnoekranową nakładkę (przykrywa też przycisk „więcej") i polega na ClickAwayListenerze
// nasłuchującym na document — stopPropagation w rodzicu (np. nagłówek karty) blokowało zamknięcie.
const LetterRowMenu = ({ options }: Props) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const enabled = options.filter((o) => o.isEnabled);

  // Klik poza przyciskiem i menu zamyka menu. Faza capture — żaden stopPropagation niżej
  // tego nie zablokuje; pointerdown działa też dotykiem.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (popupRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [open]);

  if (enabled.length === 0) return null;

  return (
    <div className={styles.rowMenu}>
      <div
        ref={triggerRef}
        className={styles.rowMenuTrigger}
        title="Więcej"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        <FontAwesomeIcon icon={faEllipsisVertical} />
      </div>
      <Popper
        anchorEl={triggerRef.current as HTMLElement}
        open={open}
        setOpen={setOpen}
        closeOnClickAway={false}
        overlay={false}
      >
        <div ref={popupRef} className={dropdownStyles.dropdownLook}>
          <div className={dropdownStyles.dropdownWrapper}>
            {enabled.map((option, index) => (
              <Fragment key={index}>
                {option.splitterBefore && <div className={dropdownStyles.splitter} />}
                <div
                  className={dropdownStyles.dropdownBox}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setOpen(false);
                    option.callback();
                  }}
                  onDoubleClick={(e) => e.stopPropagation()}
                >
                  {option.Icon && <option.Icon className={dropdownStyles.icon} />}
                  {option.name}
                </div>
                {option.splitterAfter && <div className={dropdownStyles.splitter} />}
              </Fragment>
            ))}
          </div>
        </div>
      </Popper>
    </div>
  );
};

export default LetterRowMenu;
